<template>
  <!-- Post Card Skeleton with Height Transition -->
  <transition
    name="height-expand"
    mode="out-in"
    @after-enter="onSkeletonLoaded"
  >
    <div
      v-if="loading"
      class="post-content-container skeleton-container"
      ref="skeletonRef"
      :style="computedHeightStyle"
    >
      <div class="card card--post">
        <el-skeleton :loading="loading" class="custom-dark-skeleton" animated>
          <template #template>
            <!-- Post header skeleton -->
            <div
              style="
                padding: 15px;
                display: flex;
                align-items: center;
                gap: 10px;
              "
            >
              <el-skeleton-item
                variant="circle"
                style="width: 50px; height: 50px"
              />
              <div style="flex: 1">
                <el-skeleton-item
                  variant="text"
                  style="width: 120px; height: 16px; margin-bottom: 5px"
                />
                <el-skeleton-item
                  variant="text"
                  style="width: 80px; height: 14px"
                />
              </div>
              <el-skeleton-item
                variant="text"
                style="width: 60px; height: 14px"
              />
            </div>

            <!-- Post content skeleton -->
            <div style="padding: 0 15px 15px">
              <el-skeleton-item
                variant="text"
                style="width: 90%; height: 16px; margin-bottom: 8px"
              />
              <el-skeleton-item
                variant="text"
                style="width: 75%; height: 16px; margin-bottom: 8px"
              />
              <el-skeleton-item
                variant="text"
                style="width: 85%; height: 16px; margin-bottom: 15px"
              />

              <!-- Media/Chart placeholder -->
              <el-skeleton-item
                variant="square"
                style="width: 100%; height: 200px; margin-bottom: 15px"
              />

              <!-- Footer skeleton -->
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                "
              >
                <div style="display: flex; gap: 20px">
                  <el-skeleton-item
                    variant="text"
                    style="width: 40px; height: 14px"
                  />
                  <el-skeleton-item
                    variant="text"
                    style="width: 40px; height: 14px"
                  />
                  <el-skeleton-item
                    variant="text"
                    style="width: 40px; height: 14px"
                  />
                </div>
                <el-skeleton-item
                  variant="text"
                  style="width: 50px; height: 14px"
                />
              </div>
            </div>
          </template>
        </el-skeleton>
      </div>
    </div>
  </transition>

  <!-- Actual Post Content with Height Transition -->
  <transition name="height-expand" mode="out-in" @enter="onContentEnter">
    <Lazy
      v-if="!loading"
      id="postCard"
      :unrender="false"
      :setMinHeight="isDetailView == undefined && isRepostView == undefined"
      :renderOnIdle="false"
      class="post-content-container"
      :style="computedHeightStyle"
      ref="contentRef"
    >
      <div class="p-relative post-card-inner">
        <div
          :style="{
            borderLeftColor:
              (post?.color ? post.color : 'rgb(139, 119, 207)') + ' !important',
          }"
          class="card card--post height-transition"
          :class="{
            'content-loaded': isContentStable,
            'card--repost': isRepostView,
          }"
        >
          <transition name="fade-down">
            <div
              v-if="isDetailView"
              @click.stop="viewPostDetail"
              class="post-detail-header-container"
            >
              <p>{{ $t("profileDetails.labels.Comments") }}</p>
              <i
                class="fas fa-times expand_icon"
                style="color: white"
                @click="ChangingBit()"
              ></i>
            </div>
          </transition>

          <transition name="fade-slide" @enter="onSectionLoaded">
            <span @click.stop="viewPostDetail">
              <PostInfoComponent
                :fromProfilePostCard="fromProfilePostCard"
                v-if="isRepostView == undefined && !isDetailView"
                :post="post"
              />
            </span>
          </transition>

          <div
            class="post--content"
            :class="repostPost && !isDetailView ? 'shared-post-body-inner' : ''"
          >
            <transition name="fade-scale" @after-enter="onSectionRendered">
              <span @click.stop="viewPostDetail">
                <PostProfile
                  :user="postUser"
                  :addPostModel="post"
                  :postSubType="post.postSubType"
                  :postType="post.type"
                  :postPrivacy="post.postPrivacy"
                  :postId="post._id"
                  @avatar-click="handleAvatarClick"
                  v-if="!isDetailView"
                />
              </span>
            </transition>

            <div class="body" ref="bodyRef">
              <transition name="fade-up" appear @after-enter="onTextRendered">
                <PostTextComponent :post="post" v-if="!isDetailView" />
              </transition>
              <span @click.stop="viewPostDetail">
                <transition-group
                  name="content-stagger"
                  tag="div"
                  @after-enter="onSectionRendered"
                  v-if="isDetailView == undefined || isDetailView == false"
                >
                  <PostMetaComponent
                    :meta="post.metaData"
                    v-if="
                      post.type == 'text' &&
                      post.metaData &&
                      post.metaData.length
                    "
                    :key="'meta-' + post._id"
                    @rendered="onSectionRendered"
                  />

                  <FeedCandleChart
                    :key="'chart-' + post._id"
                    :post="post"
                    v-if="post.type == 'charts' || post.postSubType == 'charts'"
                  />

                  <WatchlistContainerComponent
                    v-if="
                      post.type == 'analysis' &&
                      post.postSubType == 'watchlist'
                    "
                    :postModel="post"
                    className="boardPost"
                    :key="'watchlist-' + post._id"
                    @rendered="onSectionRendered"
                  />

                  <PredictionContainerComponent
                    v-if="
                      post.type == 'analysis' &&
                      post.postSubType == 'prediction' &&
                      post.prediction &&
                      post.prediction.length
                    "
                    :postModel="post"
                    className="boardPost"
                    :key="'prediction-' + post._id"
                    @rendered="onSectionRendered"
                  />

                  <PostSectionSelectionPreviewComponent
                    :post="post"
                    v-if="
                      post.type === 'profile' &&
                      post.postSubType == 'highlights'
                    "
                    :key="'highlights-' + post._id"
                    @rendered="onSectionRendered"
                  />

                  <PostSectionSelectionRelationPreviewComponent
                    :post="post"
                    v-if="
                      post.type === 'profile' && post.postSubType == 'relations'
                    "
                    :key="'relations-' + post._id"
                    @rendered="onSectionRendered"
                  />

                  <template
                    v-if="post.type === 'text' && post.postSubType === 'page'"
                    :key="'page-' + post._id"
                  >
                    <PostPreviewComponent
                      :show-edit-dropdown="false"
                      :type="postData?.type"
                      :content="postData?.data || false"
                      :current-page="postData?.pageNumber"
                      :total-pages="post.pagePostData?.length || 1"
                      @update:value="setPage"
                      :value="postData?.pageNumber || 1"
                      @viewPostDetail="viewPostDetail"
                      @rendered="onSectionRendered"
                    />
                  </template>

                  <template
                    v-if="
                      post.type === 'analysis' && post.postSubType == 'signal'
                    "
                    :key="'signal-' + post._id"
                  >
                    <div class="SignalCardInfoDv" v-if="post.signal">
                      <SignalCardItem
                        :addPostModelDv="post.signal"
                        @rendered="onSectionRendered"
                      />
                    </div>
                  </template>

                  <template
                    v-if="
                      post.type === 'portfolio' && post.postSubType == 'trade'
                    "
                    :key="'trade-' + post._id"
                  >
                    <PortfolioTradePostingCardComponent
                      :stock="post.stockTags[0]"
                      :postUser="postUser"
                      :trade="post.trade"
                      @rendered="onSectionRendered"
                    />
                  </template>

                  <PostMediaComponent
                    :media="post.media"
                    :post="post"
                    :showImageViewer="showImageViewer"
                    v-if="post.media && post.media.length"
                    @showPostDetail="viewPostDetail"
                    :key="'media-' + post._id"
                    @loaded="onMediaLoaded"
                  />
                </transition-group>

                <transition-group
                  name="stagger-fade"
                  tag="div"
                  @after-enter="onRepostHeightStable"
                >
                  <template
                    v-if="post.repostId && repostPost && !isRepostView"
                    :key="'repost-' + post._id"
                  >
                    <Teleport
                      v-if="isDetailView && !post.media.length"
                      :to="`#teleport-post`"
                      defer
                    >
                      <PostCardComponent
                        :post="repostPost"
                        :isRepostView="true"
                        class="shared-post"
                        @height-stabilized="onRepostHeightStable"
                      />
                      <template v-if="post.repostId && !repostPost">
                        <IconComponent
                          class="iconinfoNoDataFoundDv"
                          icon="Logo_grey.svg"
                          noRtlTransform
                        />
                        <p class="paragaphDvinfoDv">
                          Orginal Post Has Been Deleted
                        </p>
                      </template>
                    </Teleport>
                    <template
                      v-else-if="!isDetailView || post.media.length"
                      :key="'repost-content-' + post._id"
                    >
                      <PostCardComponent
                        :post="repostPost"
                        :isRepostView="true"
                        class="shared-post"
                        @height-stabilized="onRepostHeightStable"
                      />
                      <template v-if="post.repostId && !repostPost">
                        <IconComponent
                          class="iconinfoNoDataFoundDv"
                          icon="Logo_grey.svg"
                          noRtlTransform
                        />
                        <p class="paragaphDvinfoDv">
                          Orginal Post Has Been Deleted
                        </p>
                      </template>
                    </template>
                  </template>
                </transition-group>
              </span>
            </div>

            <transition name="fade-up" @after-enter="onSectionRendered">
              <PostFootComponent
                v-if="isRepostView == undefined"
                :isDetailView="isDetailView"
                :sourceId="post._id"
                :stockTags="post.stockTags"
                sourceType="post"
                :post="post"
                @like-post="handleLikePost"
                @unlike-post="handleUnlikePost"
                :fromOutside="fromOutside"
                :postingData="postingData"
              />
            </transition>
          </div>
        </div>
      </div>
    </Lazy>
  </transition>
</template>

<script lang="ts">
import { usePosts } from "../../composables/posts.composables";
import PostsModel from "../../models/posts.model";
import User from "../../models/user.model";
import utilService from "@/services/util.service";
import {
  defineComponent,
  onMounted,
  ref,
  computed,
  nextTick,
  watch,
} from "vue";
import PostFootComponent from "./PostFooterComponent.vue";
import PostInfoComponent from "./PostInfoComponent.vue";
import PostMediaComponent from "./PostMediaComponent.vue";
import PostMetaComponent from "./PostMetaComponent.vue";
import PostSectionSelectionRelationPreviewComponent from "./PostSectionSelectionRelationPreviewComponent.vue";
import PostSectionSelectionPreviewComponent from "./PostSectionSelectionPreviewComponent.vue";
import PostProfile from "./PostProfile.vue";
import PostTextComponent from "./PostTextComponent.vue";
import FeedCandleChart from "./FeedCandleChart.vue";
import WatchlistContainerComponent from "../add-post/watchlist-container/WatchlistContainerComponentPost.vue";
import Lazy from "../LazyComponent.vue";
import router from "@/router/router";
import { useRouter } from "vue-router";
import PostPreviewComponent from "../add-post/PostPreviewComponent.vue";
import SignalCardItem from "../add-post/signalCreatePost/SignalCard.vue";
import PortfolioTradePostingCardComponent from "./PortfolioTradePostingCardComponent.vue";
import PredictionContainerComponent from "./PredictionContainerComponent.vue";
import { openModal, popModal } from "jenesius-vue-modal";
import PostDetailComponentVue from "./PostDetailComponent.vue";
// import { useReactionStore } from "@/services/reaction.store";
import { useAuth } from "@/composables/public-auth-signin.composable";
import httpService from "@/services/http.service";

export default defineComponent({
  name: "PostCardComponent",
  props: [
    "post",
    "isDetailView",
    "showImageViewer",
    "isRepostView",
    "fromProfilePostCard",
    "postOnPage",
    "loading",
    "fromOutside",
    "postingData",
  ],
  emits: ["closeModal", "height-stabilized", "content-loaded"],
  components: {
    Lazy,
    PostProfile,
    PredictionContainerComponent,
    SignalCardItem,
    PostSectionSelectionRelationPreviewComponent,
    PostTextComponent,
    PostSectionSelectionPreviewComponent,
    PostFootComponent,
    PostMediaComponent,
    PostMetaComponent,
    FeedCandleChart,
    PostInfoComponent,
    WatchlistContainerComponent,
    PostPreviewComponent,
    PortfolioTradePostingCardComponent,
    PostDetailComponentVue,
  },
  setup(props, context) {
    const repostPost = ref<PostsModel>();
    const { getPostById, detailPostId } = usePosts();
    const router = useRouter();
    // const reactionStore = useReactionStore();

    // Height management refs
    const contentRef = ref<HTMLElement>();
    const skeletonRef = ref<HTMLElement>();
    const bodyRef = ref<HTMLElement>();
    const currentHeight = ref(0);
    const enableHeightTransition = ref(false);
    const isContentStable = ref(false);
    const loadedSections = ref(new Set());
    const heightUpdateTimeout = ref<NodeJS.Timeout>();
    const resizeObserver = ref<ResizeObserver>();

    const computedHeightStyle = computed(() => {
      if (!enableHeightTransition.value || currentHeight.value === 0) {
        return { height: "auto" };
      }
      return {
        height: `${currentHeight.value}px`,
        "min-height": `${currentHeight.value}px`,
        overflow: "hidden",
      };
    });

    const handleLikePost = (likePostData) => {
      // reactionStore.likePost(likePostData);
    };

    const handleUnlikePost = (unlikeData) => {
      // reactionStore.unlikePost(unlikeData);
    };

    const viewPostDetail = async () => {
      if (props.isDetailView == undefined) {
        if (props.postOnPage) {
          // console.log("PostDetailComponentVue:", props.post._id);
          detailPostId.value = props.post._id;
          setTimeout(async () => {
            const modal = await openModal(PostDetailComponentVue, {
              postOnPage: props.postOnPage,
            });
            modal.on("close", () => {
              popModal();
            });
          });
        } else {
          var route = {
            name: "postDetail",
            params: { postId: props.post._id },
            query: { page: undefined },
          };
          if (postData.value?.pageNumber) {
            route.query.page = postData.value.pageNumber.toString();
          } else {
            delete route.query;
          }

          if (router.currentRoute.value.name == "postDetail") {
            setTimeout(() => {
              router.replace(route);
            });
            router.replace({ name: "feed" });
          } else {
            router.push(route);
          }
        }
      }
    };

    const handleAvatarClick = () => {
      router.push({
        name: "ProfileUser",
        params: { username: postUser.value?.userName },
      });
    };

    const postUser = ref<User>(utilService.getUserFromPost(props.post));
    const postData = ref(props.post?.pagePostData[0]);

    const setPage = (page: any) => {
      if (Number.isNaN(page)) {
        page = 1;
      }
      postData.value = props.post?.pagePostData[page > 0 ? page - 1 : page];
      // Trigger height recalculation when page changes
      scheduleHeightUpdate();
    };

    // Enhanced Height management functions
    const calculateContentHeight = async (): Promise<number> => {
      await nextTick();
      const element = props.loading ? skeletonRef.value : contentRef.value;
      if (element) {
        return element.scrollHeight;
      }
      return 0;
    };

    const scheduleHeightUpdate = async (immediate = false) => {
      if (heightUpdateTimeout.value && !immediate) {
        clearTimeout(heightUpdateTimeout.value);
      }

      const updateHeight = async () => {
        const newHeight = await calculateContentHeight();

        if (currentHeight.value === 0) {
          // Initial height set without transition
          currentHeight.value = newHeight;
          enableHeightTransition.value = true;
        } else if (Math.abs(newHeight - currentHeight.value) > 2) {
          // Only animate if height change is significant
          currentHeight.value = newHeight;
        }

        // Mark content as stable after initial load
        if (!isContentStable.value && newHeight > 0) {
          setTimeout(() => {
            isContentStable.value = true;
            context.emit("content-loaded");
          }, 300);
        }

        // Emit height stabilization event
        context.emit("height-stabilized", newHeight);
      };

      if (immediate) {
        await updateHeight();
      } else {
        heightUpdateTimeout.value = setTimeout(updateHeight, 50);
      }
    };

    const onSkeletonLoaded = () => {
      if (skeletonRef.value) {
        currentHeight.value = skeletonRef.value.scrollHeight;
      }
    };

    const onContentEnter = () => {
      enableHeightTransition.value = true;
      scheduleHeightUpdate(true);

      // Setup resize observer for dynamic content changes
      if (contentRef.value && "ResizeObserver" in window) {
        resizeObserver.value = new ResizeObserver(() => {
          scheduleHeightUpdate();
        });
        resizeObserver.value.observe(contentRef.value);
      }
    };

    const onContentLoaded = () => {
      isContentStable.value = true;
      context.emit("content-loaded");
    };

    const onSectionLoaded = (el: Element) => {
      scheduleHeightUpdate();
    };

    const onSectionRendered = () => {
      scheduleHeightUpdate();
    };

    const onTextRendered = () => {
      scheduleHeightUpdate();
    };

    const onChartLoaded = () => {
      scheduleHeightUpdate();
    };

    const onMediaLoaded = () => {
      // Media loading might take longer, give it more time
      setTimeout(() => scheduleHeightUpdate(true), 150);
    };

    const onRepostHeightStable = () => {
      scheduleHeightUpdate();
    };

    // Watch for content changes
    watch(
      () => props.post,
      () => {
        isContentStable.value = false;
        loadedSections.value.clear();
        setTimeout(() => scheduleHeightUpdate(true), 100);
      },
      { deep: true }
    );

    watch(
      () => props.loading,
      (newLoading) => {
        if (!newLoading) {
          // When loading finishes, schedule height update
          setTimeout(() => scheduleHeightUpdate(true), 100);
        }
      }
    );

    watch(
      () => postData.value,
      () => {
        // When page data changes, recalculate height
        setTimeout(() => scheduleHeightUpdate(true), 50);
      },
      { deep: true }
    );

    onMounted(async () => {
      if (props.post && props.post.repostId && !props.isRepostView) {
        if (props.fromOutside) {
          await httpService
            .getPublicPostByIdWithoutToken(props.post.repostId)
            .then((res) => {
              if (res.isSuccess) {
                repostPost.value = res.data.data;
              }
            });
        } else {
          repostPost.value = await getPostById(props.post.repostId);
        }
      }

      // Initial height calculation
      setTimeout(() => scheduleHeightUpdate(true), 200);
    });

    // Cleanup
    const cleanup = () => {
      if (heightUpdateTimeout.value) {
        clearTimeout(heightUpdateTimeout.value);
      }
      if (resizeObserver.value) {
        resizeObserver.value.disconnect();
      }
    };

    const { showPublicSignIn } = useAuth();
    const openPublicSignInDialog = async () => {
      await showPublicSignIn({
        fromHeader: true,
        fromPostDetail: true,
        postData: props.postingData,
      });
    };

    const ChangingBit = () => {
      if (props.fromOutside) {
        openPublicSignInDialog();
      } else {
        context.emit("closeModal");
      }
    };

    return {
      ChangingBit,
      postUser,
      repostPost,
      viewPostDetail,
      postData,
      setPage,
      handleAvatarClick,
      handleLikePost,
      handleUnlikePost,
      contentRef,
      skeletonRef,
      bodyRef,
      computedHeightStyle,
      enableHeightTransition,
      isContentStable,
      onSkeletonLoaded,
      onContentEnter,
      onContentLoaded,
      onSectionLoaded,
      onSectionRendered,
      onTextRendered,
      onChartLoaded,
      onMediaLoaded,
      onRepostHeightStable,
      cleanup,
    };
  },

  beforeUnmount() {
    this.cleanup();
  },
});
</script>

<style lang="scss" scoped>
/* Enhanced Height Transition System */
.height-expand-enter-active,
.height-expand-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.height-expand-enter-from {
  opacity: 0;
  max-height: 0;
  transform: scale(0.98);
}

.height-expand-leave-to {
  opacity: 0;
  max-height: 0;
  transform: scale(0.99);
}

.height-expand-enter-to,
.height-expand-leave-from {
  opacity: 1;
  max-height: 2000px; /* Large enough to accommodate all post types */
  transform: scale(1);
}

/* Smooth height transitions for the card */
.card.card--post.height-transition {
  transition: height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.4s ease, transform 0.4s ease;
  transform-origin: top center;
  overflow: hidden;

  &.content-loaded {
    transition: height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94),
      opacity 0.5s ease, transform 0.5s ease, box-shadow 0.3s ease;
  }
}

/* Container for smooth height management */
.post-content-container {
  transition: height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    min-height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  overflow: hidden;
  position: relative;
}

.post-card-inner {
  transition: all 0.3s ease;
}

/* Skeleton container with fixed height during transition */
.skeleton-container {
  transition: height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.4s ease;
}

/* Enhanced existing transitions with height awareness */

/* Fade Scale Transition */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-scale-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-scale-leave-to {
  opacity: 0;
  transform: scale(1.05);
}

.fade-scale-enter-to,
.fade-scale-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* Slide Fade Transition */
.slide-fade-enter-active {
  transition: all 0.5s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Fade Down Transition */
.fade-down-enter-active,
.fade-down-leave-active {
  transition: all 0.3s ease;
}

.fade-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-down-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Fade Slide Transition */
.fade-slide-enter-active {
  transition: all 0.4s ease-out;
}

.fade-slide-leave-active {
  transition: all 0.3s ease-in;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* Fade Up Transition */
.fade-up-enter-active {
  transition: all 0.5s ease-out;
}

.fade-up-leave-active {
  transition: all 0.3s ease-in;
}

.fade-up-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.fade-up-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Stagger Fade for Groups */
.stagger-fade-enter-active {
  transition: all 0.5s ease;
}

.stagger-fade-leave-active {
  transition: all 0.3s ease;
}

.stagger-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.stagger-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.stagger-fade-move {
  transition: transform 0.5s ease;
}

/* Content Stagger for different post types */
.content-stagger-enter-active {
  transition: all 0.6s ease;
  transition-delay: calc(0.1s * var(--stagger-index, 0));
}

.content-stagger-leave-active {
  transition: all 0.3s ease;
}

.content-stagger-enter-from {
  opacity: 0;
  transform: translateY(25px) scale(0.95);
}

.content-stagger-leave-to {
  opacity: 0;
  transform: translateY(-15px) scale(1.02);
}

.content-stagger-move {
  transition: transform 0.6s ease;
}

/* Card hover effects */
.card.card--post {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    transition: all 0.1s ease;
  }
}

/* Smooth click interactions */
.card--post {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.3s ease;

  &:active {
    transform: scale(0.98);
  }
}

/* Smooth avatar transition */
.post--content {
  transition: all 0.3s ease;
}

/* Media content smooth appearance */
.media_card {
  transition: all 0.4s ease-in-out;
}

/* Performance optimizations */
@media (prefers-reduced-motion: reduce) {
  .height-expand-enter-active,
  .height-expand-leave-active,
  .card.card--post.height-transition,
  .post-content-container,
  .skeleton-container,
  .fade-scale-enter-active,
  .fade-scale-leave-active,
  .slide-fade-enter-active,
  .slide-fade-leave-active,
  .fade-down-enter-active,
  .fade-down-leave-active,
  .fade-slide-enter-active,
  .fade-slide-leave-active,
  .fade-up-enter-active,
  .fade-up-leave-active,
  .stagger-fade-enter-active,
  .stagger-fade-leave-active,
  .content-stagger-enter-active,
  .content-stagger-leave-active {
    transition-duration: 0.1s !important;
    animation-duration: 0.1s !important;
  }

  .card.card--post:hover {
    transform: none !important;
  }
}

/* Keep your existing styles below - they remain unchanged */
.centerPostCardComponet {
  display: flex;
  justify-content: center;
  align-items: center;
}

.customScroll {
  @include scrollbar();
}

@media (min-width: 1280px) and (max-width: 1891px) {
  .card.card--post {
    :deep(.body) {
      margin: 8px 0;

      .text_post:not(.quotedText) {
        .post_message {
          font-size: var(--font-2xs) !important;
        }
      }

      .text_post.quotedText {
        .post_message {
          font-size: var(--font-3xl) !important;
          color: #fff !important;
          font-family: Georgia, "Times New Roman", Times, serif;
          font-weight: 700;
          text-align: center;
        }
      }

      .boardPost {
        .watchlist_table_body {
          padding: 7px;
        }
      }
    }
  }
}

.paragaphDvinfoDv {
  margin: 0px;
  padding: 0px;
  text-align: center;
  color: #ffffff6e;
  line-height: 1;
  letter-spacing: 0px;
  font-size: 14px;
}

.iconinfoNoDataFoundDv {
  width: 70px;
  height: 70px;
  margin: 0px auto;
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

// Post card skeleton styles
.custom-dark-skeleton {
  .el-skeleton {
    --el-skeleton-color: var(--base-lightest) !important;
    --el-fill-color: #1d1a30 !important;
    --el-fill-color-darker: var(--base-dark) !important;
  }

  .el-skeleton__item {
    background-color: var(--base-lightest) !important;
  }

  .el-skeleton__paragraph {
    background-color: var(--base-lightest) !important;
  }
}

.post--content {
  &.shared-post-body-inner {
    .body {
      display: flex;
      flex-flow: column;
    }

    .shared-post {
      + .media_card {
        order: -1;
        margin-bottom: 13px;
      }
    }
  }
}

.card--post .info {
  z-index: 101;
}

.post-detail-header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 10px 10px;

  p {
    font-size: var(--font-2lg);
    font-weight: 500;
    color: var(--base-text-white);
    line-height: 1;
    letter-spacing: 0px;
  }

  @media screen and (min-width: 1280px) and (max-width: 1891px) {
    p {
      font-size: var(--font-2sm);
    }
  }
}
</style>
